import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { getLocationsByPostcode, getLocationById } from '@/app/actions';
import debounce from 'lodash.debounce';
import { Location24Regular } from '@fluentui/react-icons';
import { RecordModel } from 'pocketbase';

const SearchSelectLocation: React.FC<{ setLocation: (location: RecordModel) => void }> = ({ setLocation }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [displayValue, setDisplayValue] = useState('');
  const [suggestions, setSuggestions] = useState<RecordModel[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [initialLocationName, setInitialLocationName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const { data: postcodeData, error: postcodeError, isLoading: isPostcodeLoading } = useQuery({
    queryKey: ['locationsByPostcode', searchTerm],
    queryFn: () => getLocationsByPostcode(searchTerm),
    enabled: searchTerm.length > 2,
  });

  useEffect(() => {
    if (postcodeData && Array.isArray(postcodeData)) {
      setSuggestions(postcodeData);
    }
  }, [postcodeData]);

  const debouncedSearchChange = debounce((value: string) => {
    setSearchTerm(value);
  }, 300);

  const handleSearchChange = (value: string) => {
    setDisplayValue(value);
    debouncedSearchChange(value);
  };

  const handleLocationSelect = (selectedLocation: RecordModel) => {
    setLocation(selectedLocation);
    setDisplayValue(selectedLocation.locationName);
    setInitialLocationName(selectedLocation.locationName);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      if (!suggestions.find(suggestion => suggestion.locationName === displayValue)) {
        setDisplayValue(initialLocationName);
      }
    }, 300);
  };

  // Rendered in a body portal so the hero's overflow-hidden can't crop it, the
  // same treatment the breed dropdown needed; positioned against the input's
  // viewport rect.
  const [mounted, setMounted] = useState(false);
  const [menuPos, setMenuPos] = useState<
    { top: number; left: number; width: number; maxHeight: number } | null
  >(null);

  useEffect(() => setMounted(true), []);

  const updateMenuPosition = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // visualViewport accounts for the on-screen keyboard on mobile.
    const viewportH = window.visualViewport?.height ?? window.innerHeight;
    // Cap to the room below the input so the menu scrolls internally instead of
    // running off the bottom of the screen.
    const spaceBelow = viewportH - rect.bottom - 8;
    const maxHeight = Math.max(120, Math.min(288, spaceBelow));
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width, maxHeight });
  }, []);

  useEffect(() => {
    if (!showSuggestions) return;
    updateMenuPosition();
    // The menu scrolling within itself must not reposition the menu - on iOS
    // that repositioning fights the momentum scroll and the list feels stuck.
    const handleAncestorScroll = (event: Event) => {
      const target = event.target as Node | null;
      if (target && menuRef.current?.contains(target)) return;
      updateMenuPosition();
    };
    // capture:true catches scrolling of any ancestor, not just window.
    window.addEventListener('scroll', handleAncestorScroll, true);
    window.addEventListener('resize', updateMenuPosition);
    window.visualViewport?.addEventListener('resize', updateMenuPosition);
    window.visualViewport?.addEventListener('scroll', updateMenuPosition);
    return () => {
      window.removeEventListener('scroll', handleAncestorScroll, true);
      window.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('resize', updateMenuPosition);
      window.visualViewport?.removeEventListener('scroll', updateMenuPosition);
    };
  }, [showSuggestions, updateMenuPosition]);

  return (
    <div className="flex flex-col relative flex-grow">
      <div className="relative w-full">
        <input
          type="text"
          placeholder="Search for location"
          value={displayValue}
          ref={inputRef}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          onBlur={handleBlur}
          className="input input-bordered w-full border-primary pr-10"
        />
        <Location24Regular className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none" />
      </div>
      {mounted && showSuggestions && suggestions.length > 0 && menuPos &&
        createPortal(
          <ul
            ref={menuRef}
            style={{
              position: 'fixed',
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
              maxHeight: menuPos.maxHeight,
              zIndex: 9999,
              // Tell the browser this list pans vertically, so it owns the
              // scroll gesture rather than leaving it to the tap handlers.
              touchAction: 'pan-y',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
            className="bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto"
            // Keeps focus on the input, so grabbing the menu itself (its padding
            // or scrollbar) can't blur the field and close the menu underneath
            // the pointer. The item handlers below still run.
            onMouseDown={(event) => event.preventDefault()}
          >
            {suggestions.map((suggestion) => (
              <li
                key={suggestion.id}
                className="p-2 cursor-pointer hover:bg-gray-200"
                // mousedown, not click: it lands before the input's blur timer.
                // iOS only synthesises it once a tap is confirmed, so unlike a
                // pointerdown handler it cannot fire while scrolling the list.
                onMouseDown={() => handleLocationSelect(suggestion)}
              >
                {suggestion.locationName +
                  `${suggestions.filter((x) => x.locationName === suggestion.locationName).length > 1
                    ? ` (${suggestion.postcode})`
                    : ``
                  }`}
              </li>
            ))}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default SearchSelectLocation;